import {
  useEffect,
  useState,
  useCallback,
  createContext,
  useContext,
} from "react";
import "./index.css";

import heroGlassesImg from "./assets/glassesnew.png";
import allianceGroupImg from "./assets/hf_20260302_231103_664c4f6a-3b81-420b-a2fa-90e8e52d626f.jpeg";
import sdkDeveloperDeskImg from "./assets/sdk-developer-desk.webp";
import eyewearFashionImg from "./assets/DesignedforEyewear.webp";
import miniappsImg from "./assets/miniapps.webp";

/* ═══════════════════════════════════════════════
   ROUTER
   ═══════════════════════════════════════════════ */

type Page = "home" | "alliance" | "mentraos" | "developers" | "tos" | "privacy";

const RouterContext = createContext<{
  page: Page;
  navigate: (p: Page) => void;
}>({ page: "home", navigate: () => {} });

function useRouter() {
  return useContext(RouterContext);
}

const PAGES: Page[] = [
  "home",
  "alliance",
  "mentraos",
  "developers",
  "tos",
  "privacy",
];

function pathToPage(pathname: string): Page {
  const segment = pathname.replace(/^\//, "").split("/")[0] as Page;
  if (PAGES.includes(segment)) return segment;
  return "home";
}

function RouterProvider({ children }: { children: React.ReactNode }) {
  const [page, setPage] = useState<Page>(() => {
    if (typeof window !== "undefined") {
      return pathToPage(window.location.pathname);
    }
    return "home";
  });

  const navigate = useCallback((p: Page) => {
    setPage(p);
    window.history.pushState(null, "", `/${p}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setPage(pathToPage(window.location.pathname));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  return (
    <RouterContext.Provider value={{ page, navigate }}>
      {children}
    </RouterContext.Provider>
  );
}

/* ═══════════════════════════════════════════════
   OGA LOGO
   ═══════════════════════════════════════════════ */

function OGALogo({ className = "h-6" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 193 71"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="57.5283" y="28.2061" width="23" height="11" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M34.0195 0C38.9443 1.80698e-06 43.4807 0.874428 47.6279 2.62402C51.775 4.37362 55.3715 6.83661 58.417 10.0117C61.4624 13.1868 63.828 16.9128 65.5127 21.1895C67.1974 25.4014 68.0391 30.0346 68.0391 35.0889C68.0391 40.0784 67.1974 44.7116 65.5127 48.9883C63.828 53.2649 61.4624 56.9909 58.417 60.166C55.3715 63.3411 51.775 65.8041 47.6279 67.5537C43.4807 69.3033 38.9443 70.1777 34.0195 70.1777C29.1596 70.1777 24.656 69.3033 20.5088 67.5537C16.3616 65.8041 12.7325 63.3741 9.62207 60.2637C6.57655 57.0885 4.21115 53.3626 2.52637 49.0859C0.84157 44.8091 3.39095e-06 40.1433 0 35.0889C0 30.0345 0.841567 25.3686 2.52637 21.0918C4.2111 16.8152 6.57666 13.122 9.62207 10.0117C12.7325 6.83652 16.3616 4.37362 20.5088 2.62402C24.656 0.874445 29.1596 0 34.0195 0ZM112.846 0.291016C116.41 0.291017 119.844 0.777114 123.148 1.74902C126.453 2.721 129.434 4.08248 132.091 5.83203C134.748 7.58163 136.952 9.55754 138.701 11.7607L130.73 20.1201C128.916 18.3705 127.037 16.9125 125.093 15.7461C123.214 14.5798 121.238 13.6724 119.164 13.0244C117.09 12.3764 114.951 12.0527 112.748 12.0527C109.508 12.0528 106.495 12.6355 103.709 13.8018C100.987 14.9682 98.5892 16.5885 96.5156 18.6621C94.507 20.7357 92.9193 23.1659 91.7529 25.9521C90.5866 28.7385 90.0039 31.7842 90.0039 35.0889C90.0039 38.2641 90.5865 41.2777 91.7529 44.1289C92.9841 46.98 94.6693 49.4749 96.8076 51.6133C98.946 53.7516 101.409 55.4358 104.195 56.667C107.046 57.8333 110.059 58.417 113.234 58.417C115.632 58.417 117.868 58.0605 119.941 57.3477C122.015 56.6349 123.862 55.6628 125.481 54.4316C127.101 53.1356 128.332 51.6448 129.175 49.96C130.082 48.2753 130.536 46.4614 130.536 44.5176V44.4199H112.165V33.1445H138.528V33.2061H154.506L140.835 69.2061H128.199L129.607 65.5947C128.077 66.5339 126.443 67.3505 124.704 68.04C121.011 69.4656 116.993 70.1777 112.651 70.1777C107.532 70.1777 102.802 69.3033 98.46 67.5537C94.1185 65.7393 90.3278 63.2444 87.0879 60.0693C83.9127 56.8941 81.4498 53.2003 79.7002 48.9883C77.9507 44.7116 77.0762 40.0784 77.0762 35.0889C77.0762 30.1642 77.9507 25.5957 79.7002 21.3838C81.5146 17.107 84.0422 13.4131 87.2822 10.3027C90.5221 7.12766 94.3129 4.66559 98.6543 2.91602C103.061 1.16641 107.791 0.291016 112.846 0.291016ZM192.935 69.2061H179.812L165.232 30.5205C164.908 29.7429 164.454 28.5119 163.871 26.8271C163.353 25.1424 162.77 23.3277 162.122 21.3838C161.485 19.4104 160.911 17.5933 160.399 15.9326C160.358 16.0634 160.318 16.1961 160.275 16.3291C159.757 17.9491 159.206 19.6342 158.623 21.3838C158.531 21.6596 158.439 21.9335 158.347 22.2061H146.528L154.734 1.16602H166.593L192.935 69.2061ZM34.0195 12.0527C30.9741 12.0527 28.1554 12.6355 25.5635 13.8018C22.9716 14.9033 20.7356 16.491 18.8564 18.5645C16.9773 20.638 15.5192 23.0682 14.4824 25.8545C13.4456 28.6409 12.9268 31.7193 12.9268 35.0889C12.9268 38.3937 13.4456 41.472 14.4824 44.3232C15.5192 47.1095 16.9773 49.5398 18.8564 51.6133C20.7356 53.6867 22.9716 55.3063 25.5635 56.4727C28.1554 57.5742 30.974 58.125 34.0195 58.125C37.1298 58.125 39.9484 57.5742 42.4756 56.4727C45.0675 55.3063 47.3035 53.6867 49.1826 51.6133C51.0618 49.4749 52.5198 47.012 53.5566 44.2256C54.6582 41.4392 55.209 38.3937 55.209 35.0889C55.209 31.7841 54.6582 28.7385 53.5566 25.9521C52.5198 23.1657 51.0618 20.7357 49.1826 18.6621C47.3034 16.5237 45.0676 14.9034 42.4756 13.8018C39.9485 12.6355 37.1298 12.0527 34.0195 12.0527Z"
      />
    </svg>
  );
}

/* ═══════════════════════════════════════════════
   NAV
   ═══════════════════════════════════════════════ */

const NAV_LINKS: { label: string; page: Page }[] = [
  { label: "Home", page: "home" },
  { label: "Alliance", page: "alliance" },
  { label: "MentraOS", page: "mentraos" },
  { label: "Developers", page: "developers" },
];

function Navbar() {
  const { page, navigate } = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      style={{
        background: "var(--c-nav-bg)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 50,
        borderBottom: scrolled
          ? "1px solid var(--c-border)"
          : "1px solid transparent",
        boxShadow: scrolled ? "0 1px 12px var(--c-nav-shadow)" : "none",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
    >
      <div
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 72,
        }}
      >
        <button
          onClick={() => navigate("home")}
          className="nav-wordmark"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            display: "flex",
            alignItems: "center",
            color: "var(--c-logo)",
            fontFamily: "'Red Hat Display', sans-serif",
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            whiteSpace: "nowrap",
          }}
        >
          Open Glasses Alliance
        </button>

        {/* Desktop */}
        <div
          style={{ gap: 28, alignItems: "center" }}
          className="hidden md:flex"
        >
          {NAV_LINKS.map((link) => (
            <button
              key={link.page}
              onClick={() => navigate(link.page)}
              className="nav-link"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px 0",
                fontSize: 15,
                fontWeight: page === link.page ? 600 : 400,
                color:
                  page === link.page
                    ? "var(--c-text)"
                    : "var(--c-text-secondary)",
                textDecoration: "none",
                fontFamily: "inherit",
                letterSpacing: "-0.01em",
                transition: "color 0.15s",
                borderBottom:
                  page === link.page
                    ? "1.5px solid var(--c-text)"
                    : "1.5px solid transparent",
              }}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Mobile hamburger */}
        <div className="flex md:hidden">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 20,
              color: "var(--c-text)",
              padding: 6,
              lineHeight: 1,
            }}
            aria-label="Menu"
          >
            {mobileOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="md:hidden"
          style={{
            borderTop: "1px solid var(--c-border)",
            background: "var(--c-bg)",
            padding: "8px 24px 16px",
          }}
        >
          {NAV_LINKS.map((link) => (
            <button
              key={link.page}
              onClick={() => {
                navigate(link.page);
                setMobileOpen(false);
              }}
              style={{
                display: "block",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "10px 0",
                fontSize: 15,
                fontWeight: page === link.page ? 600 : 400,
                color:
                  page === link.page
                    ? "var(--c-text)"
                    : "var(--c-text-secondary)",
                fontFamily: "inherit",
                width: "100%",
                textAlign: "left",
                borderBottom: "1px solid var(--c-border-light)",
              }}
            >
              {link.label}
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

/* ═══════════════════════════════════════════════
   SUB NAV
   ═══════════════════════════════════════════════ */

function SubNav({ items }: { items: { label: string; href: string }[] }) {
  return (
    <div style={{ borderBottom: "1px solid var(--c-border)" }}>
      <div
        style={{
          maxWidth: 1000,
          margin: "0 auto",
          padding: "0 32px",
          display: "flex",
          gap: 24,
          alignItems: "center",
          height: 44,
          overflowX: "auto",
        }}
      >
        {items.map((item, i) => (
          <a
            key={i}
            href={item.href}
            className="nav-link"
            style={{
              fontSize: 13,
              color: "var(--c-text-secondary)",
              textDecoration: "none",
              whiteSpace: "nowrap",
              transition: "color 0.15s",
              letterSpacing: "-0.01em",
            }}
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   LAYOUT
   ═══════════════════════════════════════════════ */

const wrap: React.CSSProperties = {
  maxWidth: 1000,
  margin: "0 auto",
  padding: "0 24px",
};

function Section({
  children,
  style,
  id,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
  id?: string;
}) {
  return (
    <div
      id={id}
      style={{ ...wrap, paddingTop: 56, paddingBottom: 56, ...style }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div style={wrap}>
      <hr
        style={{
          border: "none",
          borderTop: "1px solid var(--c-border)",
          margin: 0,
        }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   IMAGE / VIDEO
   ═══════════════════════════════════════════════ */

function Img({
  src,
  alt,
  caption,
  aspect = "16 / 9",
  style: extraStyle,
}: {
  src: string;
  alt: string;
  caption?: string;
  aspect?: string;
  style?: React.CSSProperties;
}) {
  return (
    <figure style={{ margin: "40px 0 0", ...extraStyle }}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          width: "100%",
          display: "block",
          aspectRatio: aspect,
          objectFit: "cover",
          borderRadius: 0,
        }}
      />
      {caption && (
        <figcaption
          style={{
            fontSize: 13,
            color: "var(--c-text-tertiary)",
            marginTop: 10,
            lineHeight: 1.5,
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function VideoThumb({
  src,
  label,
  caption,
}: {
  src: string;
  label: string;
  caption?: string;
}) {
  return (
    <figure style={{ margin: 0 }}>
      <div style={{ position: "relative" }}>
        <img
          src={src}
          alt={label}
          loading="lazy"
          style={{
            width: "100%",
            display: "block",
            aspectRatio: "16 / 9",
            objectFit: "cover",
            borderRadius: 0,
            filter: "brightness(0.85)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#171717">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
          <span
            style={{
              color: "#fff",
              fontSize: 12,
              fontWeight: 500,
              textShadow: "0 1px 4px rgba(0,0,0,0.4)",
              letterSpacing: "-0.01em",
            }}
          >
            {label}
          </span>
        </div>
      </div>
      {caption && (
        <figcaption
          style={{
            fontSize: 13,
            color: "var(--c-text-tertiary)",
            marginTop: 10,
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

/* ═══════════════════════════════════════════════
   IMAGES
   ═══════════════════════════════════════════════ */

const IMAGES = {
  hero: heroGlassesImg,
  alliance: allianceGroupImg,
  eyewear: eyewearFashionImg,
  sdk: sdkDeveloperDeskImg,
  videoOverview:
    "https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=800&q=80&fit=crop&auto=format",
  videoExperience:
    "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=80&fit=crop&auto=format",
  proactive: miniappsImg,
};

/* ═══════════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════════ */

function Footer() {
  const { navigate } = useRouter();
  return (
    <footer
      style={{
        borderTop: "1px solid var(--c-border)",
        marginTop: 80,
      }}
    >
      <div
        style={{
          ...wrap,
          paddingTop: 32,
          paddingBottom: 32,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: "var(--c-text-tertiary)",
            letterSpacing: "-0.01em",
          }}
        >
          © {new Date().getFullYear()} Open Glasses Alliance™
        </span>
        <span
          style={{
            fontSize: 12,
            color: "var(--c-text-tertiary)",
            letterSpacing: "-0.01em",
          }}
        >
          <button
            onClick={() => navigate("tos")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--c-text-tertiary)",
              fontSize: 12,
              fontFamily: "inherit",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Terms of Service
          </button>
          {" · "}
          <button
            onClick={() => navigate("privacy")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--c-text-tertiary)",
              fontSize: 12,
              fontFamily: "inherit",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            Privacy Policy
          </button>
        </span>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════
   PAGE: HOME
   ═══════════════════════════════════════════════ */

function HomePage() {
  return (
    <div>
      {/* Hero Banner */}
      <div style={{ ...wrap, paddingTop: 48, paddingBottom: 24 }}>
        <div
          style={{
            background: "#FBF8F0",
            border: "1px solid #EDE9E0",
            borderRadius: 24,
            display: "grid",
            gridTemplateColumns: "2fr 3fr",
            gap: 40,
            alignItems: "center",
            padding: "40px 44px",
            overflow: "hidden",
          }}
          className="home-hero-grid"
        >
          <div>
            <img
              src={IMAGES.hero}
              alt="Smart eyewear built on open infrastructure"
              style={{
                width: "100%",
                display: "block",
                objectFit: "contain",
              }}
            />
          </div>

          <div>
            <h2 style={{ ...h2, fontSize: 24, margin: "0 0 18px" }}>
              The platform that powers smart eyewear will shape how billions of
              people see, think, and experience reality.
            </h2>

            <p style={body}>
              That platform should not belong to any single company. It should
              be open and reward everyone who builds it.
            </p>
            <p style={body}>
              The Open Glasses Alliance™ is a group of eyewear, technology, and
              industry leaders who have come together to ensure the future of
              smart eyewear is open, free, and shared by everyone. We aim to
              align incentives across users, developers, brands, hardware
              manufacturers, and AI. Together we have adopted MentraOS™, the
              first complete, open, and free smart glasses platform.
            </p>
            <p style={{ ...body, margin: 0 }}>
              We are committed to commercially deploying smart eyewear and
              services using MentraOS.
            </p>

            <div
              style={{
                marginTop: 28,
                display: "flex",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <a
                href="https://github.com/Mentra-Community/MentraOS"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "10px 24px",
                  background: "#171717",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  letterSpacing: "-0.01em",
                  textDecoration: "none",
                  borderRadius: 24,
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                className="btn-primary"
              >
                Develop for MentraOS
              </a>
              <a
                href="https://github.com/Mentra-Community/MentraOS"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  padding: "10px 24px",
                  background: "transparent",
                  color: "#171717",
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  letterSpacing: "-0.01em",
                  textDecoration: "none",
                  borderRadius: 24,
                  border: "1px solid #d4d4d4",
                  cursor: "pointer",
                  transition: "background 0.15s, border-color 0.15s",
                }}
                className="btn-secondary"
              >
                Get the Source
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAGE: ALLIANCE
   ═══════════════════════════════════════════════ */

function AlliancePage() {
  return (
    <div>
      <Section
        id="alliance-overview"
        style={{ paddingTop: 64, paddingBottom: 24 }}
      >
        {/* Side-by-side: content left, image right */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 48,
            alignItems: "start",
          }}
          className="mentraos-hero-grid"
        >
          <div>
            <h2 style={h2}>Building better glasses for everyone</h2>

            <p style={body}>
              Today, 4.5 billion people around the world wear eyeglasses.
              Glasses are humanity's oldest tool for seeing the world more
              clearly. Now they can do far more, augmenting how we hear,
              communicate, remember, and think. The smart glasses era will not
              be defined by a single device from a single company, but by
              hundreds of brands and form factors serving billions of people
              across every culture, face shape, prescription, and style. The
              Open Glasses Alliance™ is a group of technology, eyewear, and
              industry leaders who share a vision for bringing smart glasses to
              everyone, built on open infrastructure that the entire industry
              can trust.
            </p>
          </div>

          <div>
            <img
              src={IMAGES.alliance}
              alt="Smart glasses will serve billions of people across every culture, face shape, and style"
              loading="lazy"
              style={{
                width: "100%",
                display: "block",
                aspectRatio: "4 / 3",
                objectFit: "cover",
                borderRadius: 0,
                marginTop: 8,
              }}
            />
            <p
              style={{
                fontSize: 13,
                color: "var(--c-text-tertiary)",
                marginTop: 10,
                lineHeight: 1.5,
              }}
            >
              Smart glasses will serve billions of people across every culture,
              face shape, and style
            </p>
          </div>
        </div>

        <h3 style={{ ...h3, marginTop: 40 }}>Innovating in the open</h3>
        <p style={body}>
          Each member of the Open Glasses Alliance is committed to building on a
          common software platform so that apps, AI, and services work across
          every pair of smart glasses regardless of who makes them. Every major
          computing era was ultimately defined by this kind of
          intercompatibility. Smart glasses will be no different. MentraOS™ is
          open source under the MIT license, giving every member the freedom to
          use, modify, and ship on their own terms.
        </p>
      </Section>

      <Section style={{ paddingTop: 24, paddingBottom: 24 }}>
        <h3 style={h3}>Sharing in the value</h3>
        <p style={body}>
          Every computing era has concentrated wealth at the platform level
          while squeezing hardware makers. MentraOS breaks that pattern. OEMs
          earn a direct share of all ecosystem revenue generated on their
          devices, and developers keep the vast majority of what they earn. The
          companies building this category share in the value they create.
        </p>
      </Section>

      <Section style={{ paddingTop: 24, paddingBottom: 24 }}>
        <h3 style={h3}>Making the vision a reality</h3>
        <p style={body}>
          MentraOS is not just a roadmap for a fantastic future, but a complete
          platform that gives eyewear brands, manufacturers, and developers
          everything they need to build smart glasses devices, software, and
          services. We are committed to commercially deploying smart eyewear and
          services using MentraOS.
        </p>
      </Section>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAGE: MENTRAOS
   ═══════════════════════════════════════════════ */

function MentraOSPage() {
  return (
    <div>
      <Section
        id="mentraos-overview"
        style={{ paddingTop: 64, paddingBottom: 24 }}
      >
        {/* Side-by-side: content left, image right */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 48,
            alignItems: "start",
          }}
          className="mentraos-hero-grid"
        >
          <div>
            <h2 style={h2}>MentraOS</h2>

            <p style={body}>
              Smart glasses will transform how humans think, communicate, and
              experience the world. MentraOS™ aims to be the shared, open,
              universal smart glasses platform: an operating system, app store,
              and key glasses applications.
            </p>

            <h3 style={{ ...h3, marginTop: 32 }}>Designed for Eyewear</h3>
            <p style={body}>
              Smart glasses must be great eyewear first. They must be
              fashion-forward and comfortable enough to wear all day. To achieve
              this, smart glasses must use ten times less power than a phone
              while giving users constant access to apps and AI. MentraOS was
              designed from the ground up to solve these challenges as the
              operating system for eyewear.
            </p>
          </div>

          <div>
            <img
              src={IMAGES.eyewear}
              alt="Smart glasses designed for eyewear"
              loading="lazy"
              style={{
                width: "100%",
                display: "block",
                aspectRatio: "4 / 3",
                objectFit: "cover",
                borderRadius: 0,
                marginTop: 8,
              }}
            />

            {/* Resources */}
            <div
              style={{
                background: "var(--c-surface)",
                border: "1px solid var(--c-border)",
                padding: "20px 24px",
                marginTop: 20,
              }}
            >
              <h4
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--c-text)",
                  margin: "0 0 10px",
                  letterSpacing: "-0.01em",
                }}
              >
                Resources
              </h4>
              <ul
                style={{
                  margin: 0,
                  padding: "0 0 0 20px",
                  fontSize: 14,
                  lineHeight: 2.2,
                }}
              >
                <li>
                  <a
                    href="https://mentra.glass"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link"
                    style={link}
                  >
                    More information about MentraOS
                  </a>
                </li>
                <li>
                  <a
                    href="https://github.com/Mentra-Community/MentraOS"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link"
                    style={link}
                  >
                    MentraOS Open Source Project
                  </a>
                </li>
                <li>
                  <a
                    href="https://github.com/Mentra-Community/MentraOS"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link"
                    style={link}
                  >
                    MentraOS Developer SDK
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section style={{ paddingTop: 24, paddingBottom: 24 }}>
        <h3 style={h3}>Proactive Apps</h3>
        <p style={body}>
          The next generation of apps won't wait to be opened. You pass the
          grocery store and your wife's shopping list appears. Someone speaks a
          foreign language and translated captions are right there. Your next
          meeting is in 20 minutes across town and a taxi is already on the way.
          MentraOS orchestrates multiple apps running simultaneously, sharing
          sensor context, and each taking action the moment it's useful.
        </p>
      </Section>

      <Section style={{ paddingTop: 24, paddingBottom: 24 }}>
        <h3 style={h3}>Open</h3>
        <p style={body}>
          Our computing platforms are extensions of ourselves. Smart glasses
          will shape how we experience reality, and the platform that powers
          them should be owned by everyone. MentraOS is the open source (MIT
          license), shared, universal operating system for smart glasses. Users
          control their interface, developers build freely, and hardware
          companies share in the full benefit of the platform. Any company can
          use, modify, and ship with MentraOS. Any developer can read the source
          and contribute to it. The platform will continue to evolve as the
          community works together to build innovative smart glasses
          applications.
        </p>
      </Section>

      <Section style={{ paddingTop: 24, paddingBottom: 24 }}>
        <h3 style={h3}>All Apps Are Equal</h3>
        <p style={body}>
          MentraOS does not differentiate between core applications and
          third-party applications. They can all be built to have equal access
          to the glasses' capabilities. Users can set any app as the default for
          any task and fully tailor their glasses to their needs. Users and OEMs
          can also customize the default AI provider for the OS layer that
          manages apps, proactivity, and AI assistant.
        </p>
      </Section>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAGE: DEVELOPERS
   ═══════════════════════════════════════════════ */

function DevelopersPage() {
  return (
    <div>
      <Section style={{ paddingTop: 64, paddingBottom: 24 }}>
        <h2 style={h2}>Developers</h2>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          <div>
            <a
              href="https://mentra.glass"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
              style={{ ...link, fontSize: 18 }}
            >
              Learn more about development with MentraOS →
            </a>
            <p
              style={{
                ...body,
                fontSize: 15,
                color: "var(--c-text-tertiary)",
                margin: "6px 0 0",
              }}
            >
              Documentation, guides, and SDK reference.
            </p>
          </div>
          <div>
            <a
              href="https://github.com/Mentra-Community/MentraOS"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
              style={{ ...link, fontSize: 18 }}
            >
              See the source code →
            </a>
            <p
              style={{
                ...body,
                fontSize: 15,
                color: "var(--c-text-tertiary)",
                margin: "6px 0 0",
              }}
            >
              Browse the full MentraOS codebase on GitHub.
            </p>
          </div>
          <div>
            <a
              href="https://github.com/Mentra-Community/MentraOS"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
              style={{ ...link, fontSize: 18 }}
            >
              Make open source contributions to MentraOS →
            </a>
            <p
              style={{
                ...body,
                fontSize: 15,
                color: "var(--c-text-tertiary)",
                margin: "6px 0 0",
              }}
            >
              File issues, submit pull requests, and help shape the platform.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAGE ROUTER
   ═══════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════
   PAGE: TERMS OF SERVICE
   ═══════════════════════════════════════════════ */

function TermsPage() {
  return (
    <div>
      <Section style={{ paddingTop: 64, paddingBottom: 24 }}>
        <h2 style={h2}>Terms of Service</h2>
        <p style={body}>
          Mentra, Inc. is a member of the Open Glasses Alliance and operates
          this Open Glasses Alliance website as a contribution to the Alliance.
          The{" "}
          <a
            href="https://mentraglass.com/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-link"
            style={link}
          >
            Mentra Terms of Service
          </a>{" "}
          apply to Mentra's operation of this website.
        </p>
      </Section>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAGE: PRIVACY POLICY
   ═══════════════════════════════════════════════ */

function PrivacyPage() {
  return (
    <div>
      <Section style={{ paddingTop: 64, paddingBottom: 24 }}>
        <h2 style={h2}>Privacy Policy</h2>
        <p style={body}>
          Mentra, Inc. is a member of the Open Glasses Alliance and operates
          this Open Glasses Alliance website as a contribution to the Alliance.
          The{" "}
          <a
            href="https://mentraglass.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-link"
            style={link}
          >
            Mentra Privacy Policy
          </a>{" "}
          applies to Mentra's operation of this website.
        </p>
      </Section>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PAGE ROUTER
   ═══════════════════════════════════════════════ */

function PageContent() {
  const { page } = useRouter();
  switch (page) {
    case "home":
      return <HomePage />;
    case "alliance":
      return <AlliancePage />;
    case "mentraos":
      return <MentraOSPage />;
    case "developers":
      return <DevelopersPage />;
    case "tos":
      return <TermsPage />;
    case "privacy":
      return <PrivacyPage />;
    default:
      return <HomePage />;
  }
}

/* ═══════════════════════════════════════════════
   APP
   ═══════════════════════════════════════════════ */

export function App() {
  return (
    <RouterProvider>
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "var(--c-bg)",
          color: "var(--c-text)",
        }}
      >
        <Navbar />
        <main style={{ flex: 1 }}>
          <PageContent />
        </main>
        <Footer />
      </div>
    </RouterProvider>
  );
}

/* ═══════════════════════════════════════════════
   SHARED STYLES
   ═══════════════════════════════════════════════ */

const h2: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 700,
  color: "var(--c-text)",
  margin: "0 0 22px",
  letterSpacing: "-0.03em",
  lineHeight: 1.2,
  fontFamily: "inherit",
};

const h3: React.CSSProperties = {
  fontSize: 23,
  fontWeight: 700,
  color: "var(--c-text)",
  margin: "0 0 18px",
  letterSpacing: "-0.025em",
  lineHeight: 1.25,
  fontFamily: "inherit",
};

const body: React.CSSProperties = {
  fontSize: 17,
  lineHeight: 1.85,
  color: "var(--c-text-secondary)",
  margin: "0 0 18px",
  letterSpacing: "-0.01em",
};

const link: React.CSSProperties = {
  color: "var(--c-accent)",
  textDecoration: "none",
  fontSize: 15,
  fontWeight: 500,
  letterSpacing: "-0.01em",
};
